import React, {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import { TiArrowSortedUp, TiArrowSortedDown } from "react-icons/ti";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import TransactionDetailsModal from "./TransactionDetailsModal";
import { CgMore } from "react-icons/cg";
import { API_URL } from "../../../constant";
import { useAuth } from "../../../context/auth-context";
import axiosInstance from "../../../utils/axiosConfig";
import { formatAmount } from "../../../utils/FormatPerson";
import { TailSpin } from "react-loader-spinner";
import { AiOutlineCloseSquare } from "react-icons/ai";
import { FaRegEye } from "react-icons/fa";
import { IoCheckboxOutline } from "react-icons/io5";
import toast from "react-hot-toast";
import EmptyData from "../../../components/EmptyData";

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const day = date.getDate();
  const month = months[date.getMonth()];
  const year = date.getFullYear();
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? "pm" : "am";

  const formattedHours = hours % 12 || 12;
  const formattedMinutes = minutes.toString().padStart(2, "0");

  return `${month} ${day}${getDaySuffix(
    day
  )}, ${year} | ${formattedHours}:${formattedMinutes}${ampm}`;
};

const getDaySuffix = (day: number) => {
  if (day > 3 && day < 21) return "th";
  switch (day % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
};

const TransactionMonitoring = forwardRef(({ searchTerm }: any, ref) => {
  // For future API integration
  // const [loading, setLoading] = useState(false);
  // const [transactions, setTransactions] = useState([]);
  // useEffect(() => {
  //   setLoading(true);
  //   axiosInstance.get('/api/transactions')
  //     .then(res => setTransactions(res.data))
  //     .finally(() => setLoading(false));
  // }, []);

  const PAGE_SIZE = 25;
  const { isAuthenticated } = useAuth();
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [page, setPage] = useState(1);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState<number>();
  const [totalPages, setTotalPages] = useState<number>(1);
  const [refetch, setRefetch] = useState<number>(1);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [customerLoadder, setCustomerLoader] = useState<boolean>(false);
  const [openSettings, setOpenSettings] = useState<Record<number, boolean>>({});
  const [declineLoader, setDeclineLoader] = useState<boolean[]>([]);
  const [approvalLoader, setApprovalLoader] = useState<boolean[]>([]);

  // For now, use dummy data

  const handleRowClick = (txn: any) => {
    setSelectedTransaction(txn);
    setShowDetailsModal(true);
  };

  useEffect(() => {
    if (!isAuthenticated) return; // Don't fetch before the session bootstrap has confirmed one

    setCustomerLoader(true);

    const fetchData = async () => {
      try {
        const searchParam = searchTerm ? `&search=${searchTerm}` : "";

        const { data } = await axiosInstance.get(
          `${API_URL}api/rules/transactions?page=${page}&limit=${PAGE_SIZE}${searchParam}`
        );

        // console.log(data.data);

        const sortedCustomers = data.data.transactions.sort(
          (a: any, b: any) =>
            new Date(b.triggeredAt).getTime() -
            new Date(a.triggeredAt).getTime()
        );

        setTotalPages(data.data.totalPages);
        setCurrentPage(data.data.page);
        setTransactions(sortedCustomers);
      } catch (error: any) {
        console.log(error.response?.data?.message || "An error occurred");
        toast.error(error.response?.data?.message || "An error occurred");
      } finally {
        setCustomerLoader(false);
      }
    };

    const debounceTimer = setTimeout(fetchData, 600);

    return () => clearTimeout(debounceTimer);
  }, [searchTerm, page, isAuthenticated, refetch]);

  // const transactions = dummyTransactions;

  const truncateWalletAddress = (address: string) => {
    return address.slice(0, 6) + "..." + address.slice(-4);
  };

  const toggleModal = (objectId: number) => {
    setOpenSettings((prev) => ({
      ...prev,
      [objectId]: !prev[objectId], // Toggle the state for the specific object
    }));
  };
  const closeModal = (objectId: number) => {
    setOpenSettings((prev) => ({
      ...prev,
      [objectId]: !prev[objectId], // Close the modal for the specific object
    }));
  };

  useImperativeHandle(ref, () => ({
    getData: () => transactions,
  }));

  return (
    <div className="bg-[#0D0D0D] h-[calc(100vh-400px)] pb-4 w-full font-manrope flex flex-col justify-between">
      <div className="overflow-auto ">
        <table className="w-full">
          <thead className="sticky top-0 bg-[#1C2227] z-10">
            <tr className="h-[56px] flex justify-start items-center px-4">
              <th
                className="text-[#617889] text-[15px] w-[14.3%] flex items-center gap-1 cursor-pointer"
                onClick={() =>
                  setSortOrder(sortOrder === "asc" ? "desc" : "asc")
                }
              >
                Tran. ID
                <span className="flex flex-col text-[7px] ml-1">
                  <TiArrowSortedUp
                    className={
                      sortOrder === "asc" ? "text-[#BDBDBE]" : "text-[#617889]"
                    }
                  />
                  <TiArrowSortedDown
                    className={
                      sortOrder === "desc" ? "text-[#BDBDBE]" : "text-[#617889]"
                    }
                  />
                </span>
              </th>
              <th className="text-[#617889] text-[15px] w-[22.3%] text-left">
                User
              </th>
              <th className="text-[#617889] text-[15px] w-[14.3%] text-left">
                Amount
              </th>
              <th className="text-[#617889] text-[15px] w-[6.3%] text-left">
                Risk
              </th>
              <th className="text-[#617889] text-[15px] w-[22.3%] text-left">
                Trigger
              </th>
              <th className="text-[#617889] text-[15px] w-[14.3%] text-left">
                Status
              </th>
              <th className="text-[#617889] text-[15px] w-[14.3%] text-left">
                Date Logged
              </th>
              <th className="text-[#617889] text-[15px] w-[6.3%] text-center">
                &nbsp;
              </th>
            </tr>
          </thead>

          {customerLoadder ? (
            <tbody>
              <tr>
                <td colSpan={8}>
                  <div className="flex justify-center items-center h-40">
                    <TailSpin width={40} height={40} color="#BEE1FB" />
                  </div>
                </td>
              </tr>
            </tbody>
          ) : (
            <tbody>
              {transactions.length === 0 ? (
                <tr>
                  <td colSpan={8}>
                    <EmptyData />
                  </td>
                </tr>
              ) : (
                transactions.map((txn, idx) => {
                  const decline = async () => {
                    setDeclineLoader((prev) => {
                      const updated = [...prev];
                      updated[idx] = true;
                      return updated;
                    });
                    try {
                      await axiosInstance.put(
                        `${API_URL}api/rules/transactions/release`,
                        {
                          transactionId: txn?._id,
                          adminAction: "DECLINE",
                        }
                      );
                      toast.success("Transaction declined successfully");
                      setRefetch((prev: number) => prev++);
                    } catch (error: any) {
                      toast.error(
                        error.response?.data?.message || "An error occurred"
                      );
                    } finally {
                      setDeclineLoader((prev) => {
                        const updated = [...prev];
                        updated[idx] = false;
                        return updated;
                      });
                      closeModal(idx);
                    }
                  };

                  const approve = async () => {
                    setApprovalLoader((prev) => {
                      const updated = [...prev];
                      updated[idx] = true;
                      return updated;
                    });
                    try {
                      await axiosInstance.put(
                        `${API_URL}api/rules/transactions/release`,
                        {
                          transactionId: txn?._id,
                          adminAction: "APPROVE",
                        }
                      );
                      toast.success("Transaction approved successfully");
                      setRefetch((prev: number) => prev++);
                    } catch (error: any) {
                      toast.error(
                        error.response?.data?.message || "An error occurred"
                      );
                    } finally {
                      setApprovalLoader((prev) => {
                        const updated = [...prev];
                        updated[idx] = false;
                        return updated;
                      });
                      closeModal(idx);
                    }
                  };

                  return (
                    <tr
                      key={idx}
                      className="h-[56px] flex justify-start items-center px-4 border-b border-[#23282D] cursor-pointer hover:bg-[#181B1F]"
                    >
                      <td className="text-white w-[14.3%] text-[14px] text-left whitespace-nowrap overflow-hidden text-ellipsis">
                        {truncateWalletAddress(txn.transactionId)}
                      </td>
                      <td className="text-white w-[22.3%] flex flex-col items-start gap-1 text-[14px] text-left">
                        <span>{txn.userFullName}</span>
                        <span className="text-[#869AA9] text-[13px]">
                          (Level {txn.kycLevel})
                        </span>
                      </td>
                      <td className="text-white w-[14.3%] text-[14px] text-left">
                        {formatAmount(txn.triggerDetails?.amount)}{" "}
                        {txn.triggerDetails?.asset}
                      </td>
                      <td className="text-white w-[6.3%] text-[14px] text-left">
                        {txn?.riskScore}
                      </td>
                      <td className="text-white w-[22.3%] text-[14px] text-left">
                        {txn.triggerName}
                      </td>
                      <td className="w-[14.3%]">
                        <span
                          className={`${
                            txn.status.toLowerCase() === "approved"
                              ? "bg-[#0B3C1A] text-[#1ED760]"
                              : txn.status.toLowerCase() === "pending"
                              ? "bg-[#181818] text-[#CB9200]"
                              : txn.status.toLowerCase() === "rejected"
                              ? "bg-[#181818] text-red-500"
                              : "bg-red-600 text-white"
                          } px-4 py-1 rounded-full text-[10px] font-semibold`}
                        >
                          {txn.status}
                        </span>
                      </td>
                      <td className="text-white w-[14.3%] text-[14px] text-left">
                        {formatDate(txn.triggeredAt)}
                      </td>
                      <td className="w-[6.3%] flex justify-center relative">
                        <CgMore
                          onClick={() => toggleModal(idx)}
                          className="text-[#869AA9] text-xl cursor-pointer"
                        />
                        {openSettings[idx] && (
                          <div
                            onClick={() => closeModal(idx)}
                            className="fixed inset-0 flex z-50 justify-end pr-20 items-center bg-black bg-opacity-20"
                          >
                            <div
                              onClick={(e) => e.stopPropagation()}
                              className="w-[190px] bg-[#1C2227]"
                            >
                              <div
                                onClick={() => {
                                  closeModal(idx);
                                  handleRowClick(txn);
                                }}
                                className="w-full border-b border-[#13181B] flex justify-between px-4 py-3 items-center"
                              >
                                <h4 className="text-white text-[15px]">
                                  View Details
                                </h4>
                                <FaRegEye className="text-white" />
                              </div>
                              {(txn.status.toLowerCase() === "pending" ||
                                txn.status.toLowerCase() === "rejected") && (
                                <div
                                  onClick={approve}
                                  className="w-full flex justify-between px-4 border-b border-[#13181B] py-3 items-center"
                                >
                                  <h4 className="text-[#359943] text-[15px]">
                                    Approve
                                  </h4>
                                  {approvalLoader[idx] ? (
                                    <TailSpin
                                      color="#ffffff"
                                      width={20}
                                      height={20}
                                    />
                                  ) : (
                                    <IoCheckboxOutline className="text-[#359943] text-[16px]" />
                                  )}
                                </div>
                              )}
                              {(txn.status.toLowerCase() === "pending" ||
                                txn.status.toLowerCase() === "approved") && (
                                <div
                                  onClick={decline}
                                  className="w-full flex justify-between px-4 border-b border-[#13181B] py-3 items-center"
                                >
                                  <h4 className="text-red-500 text-[15px]">
                                    Decline
                                  </h4>
                                  {declineLoader[idx] ? (
                                    <TailSpin
                                      color="#ffffff"
                                      width={20}
                                      height={20}
                                    />
                                  ) : (
                                    <AiOutlineCloseSquare className="text-red-500" />
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          )}
        </table>

        {/* Pagination */}
      </div>
      <div className="flex justify-end items-center p-2">
        <div className="flex gap-2 items-center">
          <span className="text-[#869AA9] text-xs">
            {currentPage} of {totalPages} page{totalPages > 1 && "s"}
          </span>
          <button
            className={`w-[27px] h-[27px] rounded-lg flex justify-center items-center text-[#1C2227] border border-[#1C2227]`}
            onClick={() => {
              if (page === 1) {
                setPage(1);
              } else {
                setPage(page - 1);
              }
            }}
            disabled={page === 1}
          >
            <FiChevronLeft className="text-[#BDBDBE] text-xl" />
          </button>
          <button
            className={`w-[27px] h-[27px] rounded-lg font-bold text-[13px] flex bg-[#BEE1FB] justify-center items-center text-[#0D0D0D] ${
              page === 1
                ? "opacity-100 ext-[#0D0D0D] bg-[#BEE1FB]"
                : " bg-transparent text-white"
            }`}
            onClick={() => setPage(1)}
            disabled={page === 1}
          >
            {page}
          </button>

          <button
            className={`w-[27px] h-[27px] rounded-lg flex justify-center items-center text-[#1C2227] border border-[#1C2227]`}
            onClick={() => {
              if (page < totalPages) {
                setPage(page + 1);
              }
            }}
            disabled={page === totalPages}
          >
            <FiChevronRight className="text-[#BDBDBE] text-xl" />
          </button>
        </div>
      </div>

      {showDetailsModal && selectedTransaction && (
        <TransactionDetailsModal
          transaction={selectedTransaction}
          onClose={() => setShowDetailsModal(false)}
          setRefetch={setRefetch}
        />
      )}
    </div>
  );
});

export default TransactionMonitoring;
